import {
  Box,
  Collapse,
  Flex,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
} from '@chakra-ui/react'
import {
  AttachmentShortcut,
  CreateTemplateField,
  DropdownField,
  DropdownOptionItem,
  ExternalPdfFieldType,
  FieldTypes,
  getFieldTypeField,
  getUniqueRandomId,
  isCreateTemplateField,
  isDropdownOptionItem,
  isTemplateField,
  isVariableOrFieldNode,
  VariableElement as IVariableElement,
  NestedFieldItem,
  placeholderField,
  PopulatedAssessment,
  Shortcut,
  ShapeType,
  TemplateField,
  TemplateFieldElement,
  TemplateKey,
  toCamelCase,
  WithId,
} from '@hb/shared'

import { CheckIcon, CloseIcon } from '@chakra-ui/icons'
import { usePopUpMessage } from '@hb/react'
import { PropsWithChildren, useCallback, useEffect, useState } from 'react'
import { Editor, Path, Transforms } from 'slate'
import { useSlate, useSlateSelection } from 'slate-react'
import { ExternalPdfTemplateToolbarComponent, TextEditorToolbar } from '../../../types/editor'
import { Container } from '../../Container'
import { StandaloneInput } from '../../forms/Input'
import { usePdfView } from '../../PdfView/context'
import { useTemplateData } from '../../Templates/contexts'
import { useExternalPdfTemplateEditor } from '../../Templates/ExternalPdf/context'
import {
  useAddExternalPdfDataAttachment,
  useAddExternalPdfInputAttachment,
  useAddExternalPdfShapeAttachment,
} from '../../Templates/ExternalPdf/hooks'
import { PageSelect } from '../../Templates/ExternalPdf/PageSelect'
import { AttachmentStyleToolbar } from './AttachmentStyleToolbar'
import { useTemplateDropdownItems } from './hooks'
import { NestedOptionSelect } from './NestedOptionSelect'
import { useTemplateFieldSelection } from '../TemplateFieldSelectionContext'
import { RadioInputToolbar } from './RadioInputToolbar'
import { RadioOptionsEditor } from './RadioOptionsEditor'
import { RichTextRadioToolbar } from './RichTextRadioToolbar'
import { ShapeStyleToolbar } from './ShapeStyleToolbar'
import { shapeDropdownItems } from './utils'

const fieldTypes: FieldTypes[] = [
  FieldTypes.TEXT,
  FieldTypes.TEXTAREA,
  FieldTypes.DATE,
  FieldTypes.DROPDOWN,
]
// The only DROPDOWN input we support in templates is a radio group, so surface it as "Radio".
const baseFieldTypeField = getFieldTypeField(fieldTypes)
const fieldTypeField = {
  ...baseFieldTypeField,
  options: baseFieldTypeField.options.map(o =>
    isDropdownOptionItem(o) && o.id === FieldTypes.DROPDOWN ? { ...o, text: 'Radio' } : o,
  ),
}

// New radios seed two empty options for the author to fill in.
const makeSeedRadioOptions = (): DropdownOptionItem[] => {
  const first = getUniqueRandomId([])
  return [
    { id: first, text: '' },
    { id: getUniqueRandomId([first]), text: '' },
  ]
}

const CreateFieldPopoverContent: React.FC<{
  field: CreateTemplateField | null
  onCreate: (field: TemplateField) => void
  onCancel: () => void
}> = ({ field, onCreate, onCancel }) => {
  const [name, setName] = useState('')
  const [type, setType] = useState<
    FieldTypes.TEXT | FieldTypes.TEXTAREA | FieldTypes.DATE | FieldTypes.DROPDOWN | null
  >(null)
  const [options, setOptions] = useState<DropdownOptionItem[]>(makeSeedRadioOptions)
  const [radioDirection, setRadioDirection] = useState<'row' | 'column'>('row')

  const isRadio = type === FieldTypes.DROPDOWN
  const cleanedOptions = options.filter(o => o.text.trim())
  const canConfirm = !!type && !!name && (!isRadio || cleanedOptions.length > 0)

  return field ? (
    <Flex flexFlow="column">
      <Flex
        w="100%"
        borderBottom="1px solid #cdcdcd"
        flexFlow="row"
        justifyContent="space-between"
        p={2}>
        <Text fontSize="md" fontWeight={600} opacity={0.8}>
          Insert custom field
        </Text>
        <IconButton size="xs" onClick={onCancel} aria-label="cancel" icon={<CloseIcon />} />
      </Flex>
      <Flex p={2} flexFlow="row" gap={2} alignItems="flex-start">
        <Flex flexFlow="column" flex={1} gap={2} minW={0}>
          <StandaloneInput
            theme="detailed"
            field={fieldTypeField}
            onChange={setType}
            value={type}
          />
          <StandaloneInput
            theme="detailed"
            field={placeholderField}
            onChange={setName}
            value={name}
          />
          {isRadio ? (
            <RadioOptionsEditor
              options={options}
              onChange={setOptions}
              direction={radioDirection}
              onDirectionChange={setRadioDirection}
            />
          ) : null}
        </Flex>
        <IconButton
          size="sm"
          isDisabled={!canConfirm}
          onClick={() => {
            if (!type || !name) return
            if (isRadio) {
              if (!cleanedOptions.length) return
              onCreate({
                placeholder: name,
                type,
                isTemplateField: true,
                inputType: 'radio',
                options: cleanedOptions,
                radioDirection,
              } as TemplateField)
            } else {
              onCreate({ placeholder: name, type, isTemplateField: true })
            }
          }}
          bg="green.500"
          color="white"
          aria-label="confirm"
          icon={<CheckIcon filter="drop-shadow(0 0 0.5px black)" />}
        />
      </Flex>
    </Flex>
  ) : null
}

const CreateFieldPopover: React.FC<
  PropsWithChildren<{
    field: CreateTemplateField | null
    onCreate: (field: TemplateField) => void
    onCancel: () => void
  }>
> = ({ field, onCreate, children, onCancel }) => (
  <Popover isOpen={!!field} onClose={onCancel}>
    <PopoverTrigger>
      <Box w="100%">{children}</Box>
    </PopoverTrigger>
    <PopoverContent>
      <CreateFieldPopoverContent field={field} onCreate={onCreate} onCancel={onCancel} />
    </PopoverContent>
  </Popover>
)

const TemplateToolbarBody: React.FC<{
  assessment: PopulatedAssessment | null | undefined
  type: TemplateKey
  creatingField: CreateTemplateField | null
  insertField: (field: TemplateField) => void
  onItemClick: (item: NestedFieldItem) => void
  withBottomBorder?: boolean
  setCreatingField: (field: CreateTemplateField | null) => void
  additionalDropdownItems?: Record<string, object>
}> = ({
  assessment,
  type,
  creatingField,
  insertField,
  onItemClick,
  setCreatingField,
  withBottomBorder,
  additionalDropdownItems,
}) => {
  const { dropdownItems } = useTemplateDropdownItems(assessment, type)
  const allItems = { ...dropdownItems, ...additionalDropdownItems }
  const allNumChildren = Object.keys(allItems).map(key => {
    const item = allItems[key] as any
    return Object.keys(item.children ?? {}).length
  })
  const allNumAllChildren = allNumChildren.reduce((acc, curr) => acc + curr, 0)
  return (
    <CreateFieldPopover
      field={creatingField}
      onCreate={insertField}
      onCancel={() => setCreatingField(null)}>
      <Container
        style={{
          height: 'auto',
          justifyContent: 'flex-start',
          width: '100%',
          flexFlow: 'row wrap',
          boxSizing: 'border-box',
          // background: 'white',
          zIndex: 2,
          borderBottom: withBottomBorder ? '1px solid' : 'none',
          borderColor: withBottomBorder ? '#cdcdcd' : 'transparent',
        }}>
        {Object.keys(allItems).map((itemId, index) => (
          <NestedOptionSelect
            values={assessment?.mergedData?.[itemId]}
            key={`${itemId}_${index}`}
            id={itemId}
            zIndex={
              2 +
              allNumAllChildren -
              index -
              allNumChildren.slice(0, index).reduce((acc, curr) => acc + curr, 0)
            }
            onItemClick={onItemClick}
            field={allItems[itemId] as any}
          />
        ))}
      </Container>
    </CreateFieldPopover>
  )
}

export const TextEditorTemplateToolbar: TextEditorToolbar = ({ mode, version }) => {
  const {
    shortcutArgs: { assessment },
    type,
  } = useTemplateData()

  const editor = useSlate()
  const selection = useSlateSelection()
  const { showError } = usePopUpMessage()
  const { selectedFieldId } = useTemplateFieldSelection()

  const [creatingField, setCreatingField] = useState<CreateTemplateField | null>(null)

  // When a radio field element is selected, swap the field-picker for its config editor. Computed
  // inline (not memoized) because the toolbar re-renders on every editor change via useSlate, and
  // Slate mutates the node tree in place — so this always reflects the latest field config.
  const findSelectedRadio = () => {
    if (!selectedFieldId) return null
    const [entry] = Array.from(
      editor.nodes({ at: [], match: n => n.type === 'field' && n.id === selectedFieldId }),
    )
    if (!entry) return null
    const fieldNode = entry[0] as TemplateFieldElement
    if (fieldNode.field?.type !== FieldTypes.DROPDOWN) return null
    return { field: fieldNode.field as DropdownField, path: entry[1] }
  }
  const selectedRadio = findSelectedRadio()

  const insertVariableOrField = useCallback(
    (v: NestedFieldItem) => {
      const item = Object.keys(v).reduce<WithId<TemplateField | Shortcut | CreateTemplateField>>(
        (acc, curr) => (v[curr] ? { ...acc, [curr]: v[curr] } : acc),
        {} as WithId<TemplateField | Shortcut | CreateTemplateField>,
      )
      if (isCreateTemplateField(item)) {
        setCreatingField(item)
        return
      }

      const createdNode = isTemplateField(item)
        ? ({
            field: item,
            id: item.id,
            children: [{ text: v.id }],
            type: 'field',
          } as TemplateFieldElement)
        : ({
            ...item,
            children: [{ text: v.name }],
            dataType: item.type,
            isInline: true,
            type: 'variable',
          } as IVariableElement)

      if (selection) {
        if (version === 'v1') {
          Transforms.insertNodes(editor, [
            createdNode,
            {
              type: 'span',
              children: [{ text: '' }],
            },
          ])
        } else {
          // if selection is variable element, insert node after it
          const [currentNode, nodePath] = Editor.node(editor, selection.focus.path)
          const [parentNode, parentPath] = Editor.parent(editor, nodePath)
          if (isVariableOrFieldNode(currentNode)) {
            Transforms.insertNodes(editor, createdNode, {
              at: Path.next(nodePath),
              select: true,
            })
          } else if (isVariableOrFieldNode(parentNode)) {
            Transforms.insertNodes(editor, [createdNode], {
              at: Editor.after(editor, parentPath),
              select: true,
            })
          } else {
            Transforms.insertNodes(editor, createdNode, { select: true })
          }
        }
      }
    },
    [selection, editor, version],
  )

  const insertField = useCallback(
    (f: TemplateField) => {
      const id = toCamelCase(f.placeholder)
      // find existing field with same id
      const existing = Array.from(
        editor.nodes({
          match: n => n.type === 'field' && n.id === id,
        }),
      )
      if (existing.length > 0) {
        showError('Field with this name already exists')
        return
      }

      insertVariableOrField({
        ...f,
        isTemplateField: true,
        name: f.placeholder,
        id,
        path: `templateFields.${id}`,
      })
    },
    [insertVariableOrField, showError, editor],
  )

  return (
    <Collapse unmountOnExit in={mode === 'Edit'} style={{ width: '100%' }}>
      {selectedRadio ? (
        <RichTextRadioToolbar
          editor={editor}
          field={selectedRadio.field}
          path={selectedRadio.path}
        />
      ) : (
        <TemplateToolbarBody
          assessment={assessment}
          type={type}
          creatingField={creatingField}
          setCreatingField={setCreatingField}
          insertField={insertField}
          onItemClick={insertVariableOrField}
          withBottomBorder
        />
      )}
    </Collapse>
  )
}
export const ExternalPdfTemplateToolbar: ExternalPdfTemplateToolbarComponent = () => {
  const {
    shortcutArgs: { assessment },
    type,
  } = useTemplateData()

  const { scrollPageIndex } = usePdfView()

  const [pageIndex, setPageIndex] = useState(scrollPageIndex)
  useEffect(() => {
    setPageIndex(scrollPageIndex)
  }, [scrollPageIndex])

  const { showError } = usePopUpMessage()

  const [creatingField, setCreatingField] = useState<CreateTemplateField | null>(null)

  const { template, selectedAttachmentId, externalPdf } = useExternalPdfTemplateEditor()
  const selectedAttachment = selectedAttachmentId
    ? externalPdf.attachments[selectedAttachmentId]
    : null
  const selectedAttachmentType = selectedAttachment?.type ?? null
  const isRadioInput =
    selectedAttachment?.type === 'input' && selectedAttachment.field.type === FieldTypes.DROPDOWN
  const showStyleToolbar =
    !!selectedAttachmentId &&
    (selectedAttachmentType === 'data' || selectedAttachmentType === 'shape' || isRadioInput)

  const insertInputAttachment = useAddExternalPdfInputAttachment(pageIndex)
  const insertDataAttachment = useAddExternalPdfDataAttachment(pageIndex)
  const insertShapeAttachment = useAddExternalPdfShapeAttachment(pageIndex)
  const onItemClick = useCallback(
    (v: NestedFieldItem) => {
      if (!template.externalPdf) {
        showError('No external pdf found')
        return
      }
      if (v.isShapeItem) {
        insertShapeAttachment(v.shapeType as ShapeType)
        return
      }
      const item = Object.keys(v).reduce<
        WithId<TemplateField | AttachmentShortcut | CreateTemplateField>
      >(
        (acc, curr) => (v[curr] ? { ...acc, [curr]: v[curr] } : acc),
        {} as WithId<TemplateField | AttachmentShortcut | CreateTemplateField>,
      )
      if (isCreateTemplateField(item)) {
        setCreatingField(item)
        return
      }

      if (isTemplateField(item)) {
        insertInputAttachment(item.type as ExternalPdfFieldType, item.placeholder)
      } else {
        insertDataAttachment(item)
      }
    },
    [template, showError, insertInputAttachment, insertDataAttachment, insertShapeAttachment],
  )

  const insertField = useCallback(
    (f: TemplateField) => {
      if (!template.externalPdf) {
        showError('No external pdf found')
        return
      }
      const extra =
        f.type === FieldTypes.DROPDOWN
          ? { options: f.options, radioDirection: f.radioDirection }
          : undefined
      insertInputAttachment(f.type as ExternalPdfFieldType, f.placeholder, extra)
    },
    [template, showError, insertInputAttachment],
  )
  return (
    <Flex w="100%">
      <Box flex={1}>
        {showStyleToolbar ? (
          selectedAttachmentType === 'shape' ? (
            <ShapeStyleToolbar />
          ) : isRadioInput ? (
            <RadioInputToolbar />
          ) : (
            <AttachmentStyleToolbar />
          )
        ) : (
          <TemplateToolbarBody
            assessment={assessment}
            type={type}
            creatingField={creatingField}
            setCreatingField={setCreatingField}
            insertField={insertField}
            onItemClick={onItemClick}
            additionalDropdownItems={{ shapes: shapeDropdownItems }}
          />
        )}
      </Box>
      <PageSelect pageIndex={pageIndex} setPageIndex={setPageIndex} />
    </Flex>
  )
}
